/**
 * Local copies of whole shiurim so playback never depends on the network
 * mid-listen. Phones only buffer ~3 minutes ahead and cut background
 * network for web views ("stops every 6 minutes").
 *
 * The copy is written straight into Cache Storage (disk-backed) — never
 * assembled in JS memory. Holding 50–200 MB in the page made Android kill
 * the WebView every ~5 min on locked phones, the wrapper reloaded the
 * player and the cycle repeated ("the app resets every few minutes").
 * A copy left behind by a killed session is reused on the next launch.
 *
 * Falls back to a small in-memory Blob only where Cache Storage is missing.
 * Keeps at most the current + next recording (retainLocalAudio).
 */
export const LOCAL_AUDIO_CACHE = 'vaad-audio-local';
const MAX_BYTES = 200 * 1024 * 1024;
const MEMORY_MAX_BYTES = 40 * 1024 * 1024;
const MAX_FAILURES = 60;
const KEY_PREFIX = '/__local-audio/';

const entries = new Map(); // id -> { url, size, type }
const inflight = new Map(); // id -> { promise, controller, progress }

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const abortError = () => new DOMException('Aborted', 'AbortError');
const isAbort = (err, signal) => signal.aborted || (err && err.name === 'AbortError');

const cacheKey = (id) => `${window.location.origin}${KEY_PREFIX}${encodeURIComponent(id)}`;
const idFromKey = (url) => {
  const i = url.indexOf(KEY_PREFIX);
  return i < 0 ? null : decodeURIComponent(url.slice(i + KEY_PREFIX.length));
};

async function openCache() {
  try {
    if (typeof caches === 'undefined') return null;
    return await caches.open(LOCAL_AUDIO_CACHE);
  } catch {
    return null;
  }
}

export const getLocalAudio = (id) => (id && entries.get(id)) || null;

/** Drop every cached / in-progress copy except `keepIds`. */
export const retainLocalAudio = (keepIds) => {
  const keep = new Set((keepIds || []).filter(Boolean));
  for (const [id, e] of entries) {
    if (keep.has(id)) continue;
    URL.revokeObjectURL(e.url);
    entries.delete(id);
  }
  for (const [id, f] of inflight) {
    if (keep.has(id)) continue;
    f.controller.abort();
    inflight.delete(id);
  }
  openCache().then(async (cache) => {
    if (!cache) return;
    const keys = await cache.keys();
    await Promise.all(keys.map((req) => {
      const id = idFromKey(req.url);
      return id && !keep.has(id) ? cache.delete(req).catch(() => {}) : null;
    }));
  }).catch(() => {});
};

// Resumes go through the same-origin relay (`p=1`): a cross-origin fetch
// carrying a Range header needs a CORS preflight that the CDN rejects.
const resumeUrl = (streamUrl) => {
  const u = new URL(streamUrl, window.location.origin);
  u.searchParams.set('p', '1');
  return u.toString();
};

/** Resumable, retrying fetch of the whole file into `sink`.
 * `pace` (optional) is awaited before every chunk read so the background
 * copy yields the connection to live playback — without it the full-file
 * download saturates a phone's bandwidth and playback can't buffer
 * ("too busy downloading to let me listen"). */
async function pump(streamUrl, signal, state, sink, maxBytes, pace) {
  const yieldToPlayback = pace || (() => Promise.resolve());
  let received = 0;
  let total = 0;
  let type = 'audio/mpeg';
  let failures = 0;
  for (;;) {
    if (signal.aborted) throw abortError();
    try {
      const resuming = received > 0;
      const res = await fetch(resuming ? resumeUrl(streamUrl) : streamUrl, {
        headers: resuming ? { Range: `bytes=${received}-` } : {},
        signal,
        cache: 'no-store',
        credentials: 'same-origin',
      });
      let restart = !resuming;
      if (res.status === 206) {
        const m = /bytes (\d+)-\d+\/(\d+|\*)/.exec(res.headers.get('Content-Range') || '');
        if (!m || Number(m[1]) !== received) {
          if (!m || Number(m[1]) !== 0) throw new Error('bad range');
          restart = true;
        }
        if (m[2] !== '*') total = Number(m[2]);
      } else if (res.status === 200) {
        restart = true;
        total = Number(res.headers.get('Content-Length')) || 0;
      } else {
        throw new Error(`HTTP ${res.status}`);
      }
      if (total > maxBytes) {
        const err = new Error('too large');
        err.fatal = true;
        throw err;
      }
      const ct = (res.headers.get('Content-Type') || '').split(';')[0].trim();
      if (ct.startsWith('audio/')) type = ct;
      if (restart) {
        received = 0;
        await sink.reset({ total, type });
      }
      const reader = res.body.getReader();
      for (;;) {
        await yieldToPlayback();
        if (signal.aborted) { try { await reader.cancel(); } catch { /* ignore */ } throw abortError(); }
        const { done, value } = await reader.read();
        if (done) break;
        await sink.write(value);
        received += value.byteLength;
        if (state.progress) state.progress(received, total);
      }
      if (total && received < total) throw new Error('short read');
      if (!received) throw new Error('empty');
      return { received, total, type };
    } catch (err) {
      if (isAbort(err, signal) || (err && err.fatal)) throw err;
      failures += 1;
      if (failures > MAX_FAILURES) throw err;
      await sleep(Math.min(15000, 1000 * failures));
    }
  }
}

// Streams straight into Cache Storage; nothing accumulates in memory.
function cacheSink(cache, key) {
  let writer = null;
  let put = null;
  return {
    async reset(meta) {
      if (writer) { try { await writer.abort(); } catch { /* ignore */ } }
      const ts = new TransformStream();
      writer = ts.writable.getWriter();
      put = cache.put(key, new Response(ts.readable, {
        headers: { 'Content-Type': meta.type, 'X-Audio-Total': String(meta.total || 0) },
      }));
      put.catch(() => {});
    },
    write: (chunk) => writer.write(chunk),
    async finish() {
      await writer.close();
      await put;
    },
    async discard() {
      if (writer) { try { await writer.abort(); } catch { /* ignore */ } }
      writer = null;
      await cache.delete(key).catch(() => {});
    },
  };
}

function memorySink() {
  let chunks = [];
  let type = 'audio/mpeg';
  return {
    reset(meta) { chunks = []; type = meta.type; },
    write(chunk) { chunks.push(chunk); },
    finish() { return new Blob(chunks, { type }); },
  };
}

const entryFromBlob = (blob, type) => ({
  url: URL.createObjectURL(blob),
  size: blob.size,
  type: blob.type || type,
});

async function readCached(cache, key) {
  const res = await cache.match(key);
  if (!res || !res.ok) return null;
  const total = Number(res.headers.get('X-Audio-Total')) || 0;
  const blob = await res.blob();
  if (blob.size > 0 && (!total || blob.size === total)) return entryFromBlob(blob, res.headers.get('Content-Type') || 'audio/mpeg');
  await cache.delete(key).catch(() => {});
  return null;
}

async function acquire(streamUrl, id, signal, state, pace) {
  const cache = await openCache();
  if (cache) {
    const key = cacheKey(id);
    try {
      const have = await readCached(cache, key);
      if (have) {
        if (state.progress) state.progress(have.size, have.size);
        return have;
      }
    } catch { /* fall through to a fresh download */ }
    const sink = cacheSink(cache, key);
    try {
      await pump(streamUrl, signal, state, sink, MAX_BYTES, pace);
      await sink.finish();
      const stored = await readCached(cache, key);
      if (stored) return stored;
      throw new Error('cache write lost');
    } catch (err) {
      await sink.discard();
      if (isAbort(err, signal) || (err && err.fatal)) throw err;
    }
  }
  const sink = memorySink();
  const { type } = await pump(streamUrl, signal, state, sink, MEMORY_MAX_BYTES, pace);
  return entryFromBlob(sink.finish(), type);
}

/**
 * Download the whole recording (resumable, retries for minutes) and cache
 * it. Concurrent calls for the same id share one download; `onProgress`
 * from the latest caller wins.
 */
export const prefetchLocalAudio = (id, streamUrl, onProgress, pace) => {
  const have = entries.get(id);
  if (have) return Promise.resolve(have);
  const running = inflight.get(id);
  if (running) {
    if (onProgress) running.progress = onProgress;
    return running.promise;
  }
  const controller = new AbortController();
  const record = { controller, progress: onProgress, promise: null };
  const state = { progress: (got, total) => { if (record.progress) record.progress(got, total); } };
  record.promise = acquire(streamUrl, id, controller.signal, state, pace)
    .then((entry) => {
      if (controller.signal.aborted) {
        URL.revokeObjectURL(entry.url);
        throw abortError();
      }
      entries.set(id, entry);
      return entry;
    })
    .finally(() => {
      if (inflight.get(id) === record) inflight.delete(id);
    });
  inflight.set(id, record);
  return record.promise;
};
